import { useState } from "react";
import { CalendarDays, GraduationCap, Wand2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AGE_MIN,
  AGE_MAX,
  MONTH_NAMES,
  ageFromParts,
  currentSchoolYear,
  daysInMonth,
  gradeFromFirstGradeYear,
  gradeLabel,
  ageLabel,
  suggestedFirstGradeYear,
} from "@/lib/students";

/** Radix reserves the empty string, so "not said" needs a value of its own. */
const NONE = "none";

/**
 * Who the learner is, in two numbers that stay true on their own.
 *
 * There is no age field. An age typed once goes stale on the next birthday, and
 * it cannot answer which grade the child is in — a September-born and a May-born
 * eleven-year-old are not in the same classroom. So the age is derived from the
 * birth year and shown, never asked for.
 *
 * Every part is optional, and the parts of a birth date are a prefix: the year
 * is the only one the app reads, the month and the day only sharpen it. A parent
 * who knows their child was born in 2015 and would have to go and look up the
 * day can say the year and stop — where a single date field demanded all three
 * or nothing.
 *
 * The first grade is a *year*, not a date. A school year is named for the autumn
 * it begins in, so the 1st of September was the only answer anybody was ever
 * going to give to a day-and-month field.
 */
export function SchoolDetailsFields({
  birthYear,
  birthMonth,
  birthDay,
  firstGradeYear,
  disabled,
}: {
  birthYear?: number | null;
  birthMonth?: number | null;
  birthDay?: number | null;
  firstGradeYear?: number | null;
  disabled?: boolean;
}) {
  const [year, setYear] = useState(birthYear ? String(birthYear) : "");
  const [month, setMonth] = useState(birthMonth ? String(birthMonth) : NONE);
  const [day, setDay] = useState(birthDay ? String(birthDay) : NONE);
  const [firstGrade, setFirstGrade] = useState(firstGradeYear ? String(firstGradeYear) : "");

  const thisYear = new Date().getFullYear();

  // A half-typed year is not a year. Read literally, "20" on the way to "2015"
  // is a 2006-year-old whose first grade was suggested as 27 — so everything
  // derived below waits until the year could be a real one. Same bounds the
  // server validates, expressed as the ages it will accept.
  const typedYear = year ? Number(year) : null;
  const plausibleYear =
    typedYear && typedYear >= thisYear - AGE_MAX && typedYear <= thisYear - AGE_MIN
      ? typedYear
      : null;

  const parts = {
    year: plausibleYear,
    month: month === NONE ? null : Number(month),
    day: day === NONE ? null : Number(day),
  };

  const derivedAge = ageFromParts(parts);
  const firstGradeParsed = firstGrade.length === 4 ? Number(firstGrade) : null;
  const derivedGrade = firstGradeParsed ? gradeFromFirstGradeYear(firstGradeParsed) : null;

  // Offered rather than filled in: a child who started early, late, or repeated
  // a year is common enough that guessing silently would be wrong for them and
  // invisible to whoever is filling this in.
  const suggestion = plausibleYear ? suggestedFirstGradeYear(plausibleYear) : null;
  const canSuggest = suggestion !== null && String(suggestion) !== firstGrade;

  const dayCount = parts.month ? daysInMonth(parts.year, parts.month) : 31;

  // Clearing the year takes the month and the day with it — a day in March
  // belonging to no year is not a birth date, and the server refuses one.
  function changeYear(value: string) {
    setYear(value);
    if (!value) {
      setMonth(NONE);
      setDay(NONE);
    }
  }

  // Likewise a day belongs to a month, and February has no 31st.
  function changeMonth(value: string) {
    setMonth(value);
    if (value === NONE) setDay(NONE);
    else if (day !== NONE && Number(day) > daysInMonth(parts.year, Number(value))) setDay(NONE);
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="birthYear" className="flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
          Gimimo data
        </Label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            id="birthYear"
            name="birthYear"
            type="number"
            inputMode="numeric"
            className="w-24"
            placeholder="Metai"
            min={thisYear - AGE_MAX}
            max={thisYear - AGE_MIN}
            value={year}
            onChange={(e) => changeYear(e.target.value)}
            disabled={disabled}
          />

          <Select value={month} onValueChange={changeMonth} disabled={disabled || !parts.year}>
            {/* Wide enough for „Mėnuo (nežinau)", which is what the trigger reads
                until a month is picked — narrower it clipped mid-word. */}
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Mėnuo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Mėnuo (nežinau)</SelectItem>
              {MONTH_NAMES.map((name, index) => (
                <SelectItem key={name} value={String(index + 1)}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={day} onValueChange={setDay} disabled={disabled || !parts.month}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Diena" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Diena (nežinau)</SelectItem>
              {Array.from({ length: dayCount }, (_, i) => i + 1).map((d) => (
                <SelectItem key={d} value={String(d)}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Carried for the parent's FormData, which cannot read a Select. */}
          <input type="hidden" name="birthMonth" value={month === NONE ? "" : month} />
          <input type="hidden" name="birthDay" value={day === NONE ? "" : day} />
        </div>
        <p className="text-sm text-muted-foreground">
          Užtenka metų — pagal juos parenkame užduočių sunkumą. Mėnuo ir diena neprivalomi.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="firstGradeYear" className="flex items-center gap-1.5">
          <GraduationCap className="h-3.5 w-3.5 text-muted-foreground" />
          Pradėjo 1 klasę
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id="firstGradeYear"
            name="firstGradeYear"
            type="number"
            inputMode="numeric"
            className="w-28"
            placeholder="Metai"
            min={thisYear - AGE_MAX}
            max={currentSchoolYear()}
            value={firstGrade}
            onChange={(e) => setFirstGrade(e.target.value)}
            disabled={disabled}
          />
          {canSuggest && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              title="Įprasti metai: kai vaikui sueina 7"
              onClick={() => setFirstGrade(String(suggestion))}
              disabled={disabled}
              className="shrink-0"
            >
              <Wand2 className="mr-1.5 h-4 w-4" />
              {suggestion}
            </Button>
          )}
        </div>
      </div>

      <p className="rounded-md bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
        {derivedAge != null ? ageLabel(derivedAge) : "amžius nenurodytas"}
        {" · "}
        {derivedGrade != null ? (
          <span className="font-medium text-foreground">{gradeLabel(derivedGrade)}</span>
        ) : derivedAge != null ? (
          <span>klasė spėjama pagal metus</span>
        ) : (
          <span>klasė nežinoma</span>
        )}
        {derivedGrade != null && ". Klasė pasikeis pati kiekvieną vasarą."}
      </p>
    </div>
  );
}
